import classNames from 'classnames'
import { h, JSX } from 'preact'

import { SeatIcon } from '../icon/icon'

interface FloorplanSymbol {
    class?: string
    text: string
}

export type TextKey = 'screen' | 'entrance' | 'stage' | 'balcony' | 'sideBalcony'

export interface FloorplanProps {
    modifier?: string
    class?: string
    texts: Record<TextKey, string>
    data: FloorplanData
    onClickSeat?: (rowNumber: number, seatNumber: number) => unknown
    showBorder?: boolean
}

export interface Seat {
    number: number
    isBooked?: boolean
    isTaken?: boolean
    isDisabled?: boolean
    icon?: string
}

export interface SeatRow {
    number: number
    seats: (Seat | null)[]
    numberOnLeft?: boolean
    numberOnRight?: boolean
    icon?: string
}

export type CurveType = 'none' | 'arc' | 'quadratic'

export interface SeatGrid {
    id: string
    x: number
    y: number
    rotation?: number
    rows: SeatRow[]
    curve?: {
        path: string // SVG path string
        spacing: number // Spacing between seats along the curve
        offset?: number // Optional offset from the start of the path
        type?: CurveType // Type of curve to use
        intensity?: number // How much the curve bends
        direction?: 'up' | 'down' // Direction of the curve
    }
}

export type LabelStyle = 'small' | 'large'

export interface Label {
    x: number
    y: number
    rotation?: number
    textKey: TextKey
    style: LabelStyle
}

export interface Pos {
    x: number
    y: number
}

export interface FloorplanData {
    width: number
    height: number
    grids: SeatGrid[]
    labels?: Label[]
    screenTextPos?: Pos // Backwards compatibility with older floorplans, can be removed soon
    entryTextPos?: Pos // Backwards compatibility with older floorplans, can be removed soon
    symbols?: FloorplanSymbol[]
    minWidth?: number
    maxWidth?: number
    maxHeight?: number
}

const SEAT_SIZE = 24
const ROW_HEIGHT = 32

export const iconNames = [
    'super',
    'down',
    'pair-left',
    'pair-right',
    'pari-left-down',
    'pari-right-down',
    'wheelchair',
    'wheelchair-down',
]

const getSeatBackgroundPos = (iconName: string | undefined): { x: number; y: number } => {
    if (
        iconName === 'super' ||
        iconName === 'down' ||
        iconName === 'pair-left' ||
        iconName === 'pair-right' ||
        iconName === 'pari-left-down' ||
        iconName === 'pari-right-down' ||
        iconName === 'wheelchair-down'
    ) {
        return { x: -3, y: -3 }
    }

    return { x: -3, y: 1.5 }
}

const getSeatIconPath = (iconName: string | undefined) => {
    if (iconName === 'super') {
        return 'M1 0h15s1 0 1 1v3s0 1-1 1H1S0 5 0 4V1s0-1 1-1M1 5.536h2s1 0 1 1V17s0 1-1 1H1s-1 0-1-1V6.536s0-1 1-1M14 5.536h2s1 0 1 1V17s0 1-1 1h-2s-1 0-1-1V6.536s0-1 1-1M4.414 5.467h8.104v11.52H4.414z'
    } else if (iconName === 'pair-left') {
        return 'M20 5H4.5c-2 0-3.8 1.5-4 3.6l-.5 5C-.2 15.9 1.6 18 4 18h16zm0-5H3.1c-.8 0-1.5.7-1.5 1.5S2.3 3 3.1 3H20Z'
    } else if (iconName === 'pair-right') {
        return 'M-2 5h15.5c2 0 3.8 1.5 4 3.6l.5 5c.2 2.3-1.6 4.4-4 4.4H-2Zm0-5h16.9c.8 0 1.5.7 1.5 1.5S15.7 3 14.9 3H-2Z'
    } else if (iconName === 'down') {
        return 'M.6 8.6c.2-2 1.9-3.6 4-3.6h8c2 0 3.8 1.5 4 3.6l.5 5c.2 2.3-1.6 4.4-4 4.4H4c-2.4 0-4.2-2.1-4-4.4l.6-5ZM3 0h11c.8 0 1.5.7 1.5 1.5S14.8 3 14 3H3c-.8 0-1.5-.7-1.5-1.5S2.2 0 3 0Z'
    } else if (iconName === 'wheelchair') {
        return 'M0.361015 8.83218C0.166676 6.50011 2.00704 4.5 4.3472 4.5H13.6528C15.993 4.5 17.8333 6.5001 17.639 8.83218L16.8057 18.8322C16.6329 20.9053 14.8998 22.5 12.8195 22.5H5.18053C3.10018 22.5 1.36711 20.9054 1.19435 18.8322L0.361015 8.83218ZZ'
    } else if (iconName === 'wheelchair-down') {
        return 'M17.639 13.6678C17.8333 15.9999 15.993 18 13.6528 18L4.3472 18C2.00704 18 0.166676 15.9999 0.361016 13.6678L1.19435 3.66782C1.36711 1.59465 3.10018 -1.30258e-06 5.18053 -1.12071e-06L12.8195 -4.52896e-07C14.8998 -2.71026e-07 16.6329 1.59465 16.8057 3.66782L17.639 13.6678Z'
    } else if (iconName === 'pari-left-down') {
        return 'M20 19H4.5c-2 0-3.8-1.5-4-3.6l-.5-5C-.2 8.1 1.6 6 4 6h16zm0 5H3.1c-.8 0-1.5-.7-1.5-1.5S2.3 21 3.1 21H20Z'
    } else if (iconName === 'pari-right-down') {
        return 'M-2 19h15.5c2 0 3.8-1.5 4-3.6l.5-5c.2-2.3-1.6-4.4-4-4.4H-2Zm0 5h16.9c.8 0 1.5-.7 1.5-1.5S15.7 21 14.9 21H-2Z'
    } else {
        return 'M16.5 13.9c-.2 2-1.9 3.6-4 3.6h-8c-2 0-3.8-1.5-4-3.6l-.5-5c-.2-2.3 1.6-4.4 4-4.4h9.1c2.4 0 4.2 2.1 4 4.4l-.6 5zm-2.4 8.6h-11c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5h11c.8 0 1.5.7 1.5 1.5s-.7 1.5-1.5 1.5z'
    }
}

const getWheelchairIconPaths = (iconName: 'wheelchair' | 'wheelchair-down'): JSX.Element => {
    return (
        <g transform={iconName === 'wheelchair-down' ? 'translate(0 -4.5)' : ''}>
            <path
                d="M10.7162 16.0016C10.3939 17.2541 9.25704 18.1795 7.90385 18.1795C6.3002 18.1795 5 16.8793 5 15.2756C5 14.1037 5.69426 13.09388 6.69391 12.63508M7.90385 10.43589L8.38783 14.7917L12.0176 14.5497L12.7436 17.2115H13.4696M7.90385 10.43589C8.16057 10.43589 8.40678 10.33392 8.5883 10.15239C8.76983 9.97087 8.87181 9.72466 8.87181 9.46795C8.87181 9.21123 8.76983 8.96503 8.5883 8.78351C8.40678 8.60198 8.16058 8.5 7.90386 8.5C7.64714 8.5 7.40094 8.60198 7.21942 8.78351C7.03789 8.96503 6.93591 9.21123 6.93591 9.46795C6.93591 9.72466 7.03789 9.97087 7.21942 10.15239C7.40094 10.33392 7.64714 10.43589 7.90385 10.43589ZM9.59777 12.3718H11.5337"
                stroke="black"
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M8.87181 9.46795C8.87181 9.72466 8.76983 9.97087 8.5883 10.15239C8.40678 10.33392 8.16057 10.43589 7.90385 10.43589C7.64714 10.43589 7.40094 10.33392 7.21942 10.15239C7.03789 9.97087 6.93591 9.72466 6.93591 9.46795C6.93591 9.21123 7.03789 8.96503 7.21942 8.78351C7.40094 8.60198 7.64714 8.5 7.90386 8.5C8.16058 8.5 8.40678 8.60198 8.5883 8.78351C8.76983 8.96503 8.87181 9.21123 8.87181 9.46795ZM9.59777 12.3718H11.5337H9.59777Z"
                fill="black"
            />
        </g>
    )
}

const renderSeat = (props: FloorplanProps, row: SeatRow, seat: Seat, seatIndex: number) => {
    const iconName = seat.icon ?? row.icon

    const isWheelChair = iconName === 'wheelchair' || iconName === 'wheelchair-down'

    return (
        <g
            key={seat.number}
            class={classNames('floorplan__seat', {
                'is-booked': seat.isBooked,
                'is-taken': seat.isTaken,
                'is-disabled': seat.isDisabled,
            })}
            transform={`translate(${seatIndex * SEAT_SIZE} 0)`}
            onClick={() => props.onClickSeat?.(row.number, seat.number)}
        >
            <rect
                class="floorplan__seat-background"
                {...getSeatBackgroundPos(iconName)}
                width="24"
                height="24"
            />
            <path d={getSeatIconPath(iconName)} />
            {isWheelChair ? (
                getWheelchairIconPaths(iconName)
            ) : (
                <text
                    className="floorplan__seat-number"
                    x="8.5"
                    y="12"
                    text-anchor="middle"
                    dominant-baseline="middle"
                >
                    {seat.number}
                </text>
            )}
        </g>
    )
}

const renderRow = (props: FloorplanProps, row: SeatRow, rowIndex: number) => {
    let first = -1
    let last = -1

    for (const [index, seat] of row.seats.entries()) {
        if (seat) {
            last = index

            if (first === -1) {
                first = index
            }
        }
    }

    return (
        <g key={row.number} transform={`translate(0 ${rowIndex * ROW_HEIGHT})`}>
            {row.numberOnLeft && (
                <text
                    class="floorplan__row-number"
                    x={first * SEAT_SIZE - 6}
                    y={SEAT_SIZE / 2}
                    text-anchor="end"
                    dominant-baseline="middle"
                >
                    {row.number}
                </text>
            )}
            {row.seats.map((seat, seatIndex) => seat && renderSeat(props, row, seat, seatIndex))}
            {row.numberOnRight && (
                <text
                    class="floorplan__row-number"
                    x={last * SEAT_SIZE + 23}
                    y={SEAT_SIZE / 2}
                    dominant-baseline="middle"
                >
                    {row.number}
                </text>
            )}
        </g>
    )
}

interface PathPosition {
    x: number
    y: number
    angle: number
}

const positionAlongPath = (pathElement: SVGPathElement, distance: number): PathPosition => {
    const point = pathElement.getPointAtLength(distance)
    // Get the tangent angle at this point for proper seat rotation
    const nextPoint = pathElement.getPointAtLength(distance + 1)
    const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * (180 / Math.PI)
    return { x: point.x, y: point.y, angle }
}

const renderGrid = (props: FloorplanProps, grid: SeatGrid) => {
    if (grid.curve) {
        const curve = grid.curve
        // Create a temporary path element to calculate positions
        const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        tempPath.setAttribute('d', curve.path)

        return (
            <g key={grid.id} transform={`translate(${grid.x} ${grid.y})`}>
                {grid.rows.map((row, rowIndex) => {
                    const rowOffset = rowIndex * ROW_HEIGHT
                    const baseOffset = curve.offset || 0
                    const spacing = curve.spacing

                    return (
                        <g key={row.number}>
                            {row.seats.map((seat, seatIndex) => {
                                if (!seat) return null

                                const distance = baseOffset + seatIndex * spacing
                                const { x, y, angle } = positionAlongPath(tempPath, distance)

                                return (
                                    <g
                                        key={seat.number}
                                        transform={`translate(${x} ${y + rowOffset}) rotate(${angle})`}
                                    >
                                        {renderSeat(props, row, seat, 0)}
                                    </g>
                                )
                            })}
                        </g>
                    )
                })}
            </g>
        )
    }

    // Original straight layout rendering
    return (
        <g key={grid.id} transform={`translate(${grid.x} ${grid.y}) rotate(${grid.rotation ?? 0})`}>
            {grid.rows.map((row, rowIndex) => renderRow(props, row, rowIndex))}
        </g>
    )
}

export const Floorplan = (props: FloorplanProps): JSX.Element => {
    console.log('Floorplan props:', props)
    console.log('Floorplan data:', props.data)
    console.log('Floorplan grids:', props?.data?.grids)
    // Add safety check at the beginning
    if (!props.data?.grids) {
        console.warn('Floorplan: Missing data or grids')
        return (
            <div class={classNames('floorplan', props.modifier, props.class)}>
                <div class="floorplan__plan">
                    <div class="floorplan__plan-inner">
                        <svg
                            viewBox="0 0 100 100"
                            style={{
                                minWidth: props.data?.minWidth,
                                maxWidth: props.data?.maxWidth,
                                maxHeight: props.data?.maxHeight,
                            }}
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div class={classNames('floorplan', props.modifier, props.class)}>
            <div class="floorplan__plan">
                <div class="floorplan__plan-inner">
                    <svg
                        viewBox={`0 0 ${props.data.width} ${props.data.height}`}
                        style={{
                            minWidth: props.data.minWidth,
                            maxWidth: props.data.maxWidth,
                            maxHeight: props.data.maxHeight,
                        }}
                    >
                        {props.showBorder && (
                            <rect
                                x="1"
                                y="1"
                                width={props.data.width - 2}
                                height={props.data.height - 2}
                                fill="none"
                                stroke="red"
                            />
                        )}
                        {props.data.grids.map((grid) => renderGrid(props, grid))}
                        {props.data.labels?.map((label, index) => (
                            <text
                                key={index}
                                class={classNames('floorplan__text', {
                                    'floorplan__text--small': label.style === 'small',
                                    'floorplan__text--large': label.style === 'large',
                                })}
                                transform={`translate(${label.x} ${label.y}) rotate(${label.rotation ?? 0})`}
                                text-anchor="middle"
                                dominant-baseline="middle"
                            >
                                {props.texts[label.textKey]}
                            </text>
                        ))}

                        {/* Backwards compatibility with older floorplans, can be removed soon */}
                        {props.data.screenTextPos && (
                            <text
                                class="floorplan__text floorplan__text--large"
                                x={props.data.screenTextPos.x}
                                y={props.data.screenTextPos.y}
                                text-anchor="middle"
                                dominant-baseline="middle"
                            >
                                {props.texts.screen}
                            </text>
                        )}
                        {props.data.entryTextPos && (
                            <text
                                class="floorplan__text floorplan__text--small"
                                x={props.data.entryTextPos.x}
                                y={props.data.entryTextPos.y}
                                text-anchor="middle"
                                dominant-baseline="middle"
                            >
                                {props.texts.entrance}
                            </text>
                        )}
                    </svg>
                </div>
            </div>
            {props.data.symbols && (
                <div class="floorplan__symbols">
                    {props.data.symbols.map((symbol, index) => (
                        <div key={index} class={classNames('floorplan__symbol', symbol.class)}>
                            <SeatIcon class={classNames('floorplan__symbol-icon', symbol.class)} />
                            <span class="floorplan__symbol-text">{symbol.text}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
